import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '../FormField';
import PrimaryCurrencyField from '../settings/PrimaryCurrencyField';
import type { ProjectCurrency } from '../../utils/currency';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../NotificationProvider';
import { reportSaveError, useFieldErrors } from '../forms/_shared';
import AccountFormPage from './AccountFormPage';

const LIST_PATH = '/pipeline';

/** New lead, as a page. Adds a card at the Lead stage of the pipeline. */
export const LeadFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { createLead } = useAuth();
  const { showError, showSuccess } = useNotifications();
  const fieldErrors = useFieldErrors();
  const [name, setName] = useState('');
  const [currency, setCurrency] = useState<ProjectCurrency>('');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!name.trim() || !currency || saving) return;
    fieldErrors.clear();
    setSaving(true);
    try {
      await createLead(name.trim(), currency);
      showSuccess('Lead added.');
      navigate(LIST_PATH);
    } catch (err) {
      reportSaveError(err, fieldErrors, showError, 'Failed to add lead.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <AccountFormPage
      view="lead-form"
      title="New lead"
      subtitle="Adds a card at the Lead stage, the same project you would get from Projects, just earlier in its lifecycle."
      onSave={handleSave}
      onCancel={() => navigate(LIST_PATH)}
      saveLabel="Add lead"
      canSave={!!name.trim() && !!currency}
      saving={saving}
    >
      <Input label="Name" required error={fieldErrors.errorFor('name')} value={name} onChange={e => { setName(e.target.value); fieldErrors.clear('name'); }} placeholder="e.g. Alex & Sam's Wedding" autoFocus />
      <PrimaryCurrencyField value={currency} locked={false} onChange={c => { setCurrency(c); fieldErrors.clear('currency'); }} />
    </AccountFormPage>
  );
};

export default LeadFormPage;
